import { Fragment, useId, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, List, ListItem, ListItemButton, ListItemText, Popper, Toolbar, Typography } from '@mui/material';

import { useBreakpointFlags } from 'hooks/useBreakpointFlags';
import { CrossIcon as CloseIcon } from 'assets/icons';

const getSubItemId = (baseId, label) => `${baseId}${label.replaceAll(' ', '-')}`;

const styles = {
  toolbarWrapper: ({ zIndex }) => ({
    width: '100%',
    position: 'fixed',
    bottom: 50,
    left: 0,
    zIndex: zIndex.appBar,
  }),
  toolbar: ({ palette }, buttonsCount) => ({
    width: 1067,
    height: 78,
    margin: 'auto',
    padding: 'unset !important',
    display: 'grid',
    gridTemplateColumns: `85px minmax(190px, 1fr) repeat(${buttonsCount}, 1fr) 100px`,
    gap: '2px',
    borderRadius: '10px',
    backgroundColor: palette.colors.lightGrey,
  }),
  item: ({ palette }) => ({
    height: '100%',
    backgroundColor: palette.text.primary,
    color: palette.text.secondary,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    paddingBlock: 'unset !important',
    paddingInline: '8px !important',
    borderRadius: 0,
    minWidth: 'unset !important',
    '&:hover': {
      backgroundColor: palette.text.primary,
    },
  }),
  countItem: ({ palette }) => ({
    backgroundColor: palette.colors.statusBlue,
    borderRadius: '10px 0 0 10px',
    cursor: 'default',
    '&:hover': {
      backgroundColor: palette.colors.statusBlue,
    },
  }),
  textItem: {
    padding: 'unset !important',
    cursor: 'default',
  },
  closeButton: ({ palette }) => ({
    marginLeft: 0.5,
    boxShadow: `-4px 0px 4px -1px ${palette.colors.darkGrey}`,
    borderRadius: '0 10px 10px 0',
    '&:hover': {
      boxShadow: `-4px 0px 4px -1px ${palette.colors.darkGrey}`,
    },
  }),
  buttonIcon: {
    width: 20,
    height: 20,
  },
  subMenu: {
    borderRadius: '10px',
    overflow: 'hidden',
    marginBottom: 0.5,
  },
  subItem: ({ palette }) => ({
    width: 250,
    height: 48,
    borderColor: palette.colors.darkLilac,
  }),
  subItemButton: ({ palette }) => ({
    height: '100%',
    backgroundColor: palette.colors.liteLilac,
    color: palette.text.secondary,
    padding: 'unset !important',
    '&:hover': {
      backgroundColor: palette.colors.darkLilac,
    },
  }),
};

export const StickyBar = ({ selectedItemsCount, buttons, onCloseBar }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const baseId = useId();

  const { isLgUp: isLgWidth } = useBreakpointFlags();

  const barButtons = buttons.map(({ subItems, ...buttonProps }) => ({
    ...buttonProps,
    subItems: subItems?.length
      ? subItems.map((subItem) => ({ ...subItem, subItemId: getSubItemId(baseId, subItem.label) }))
      : [],
  }));

  const handleButtonClick = (event, onClick, subItems = [], buttonLabel) => {
    if (subItems.length) {
      setAnchorEl(event.currentTarget);
      setActiveSubMenu((prevState) => (prevState === buttonLabel ? null : buttonLabel));

      return;
    }

    setAnchorEl(null);
    setActiveSubMenu(null);
    onClick();
  };

  const handleCloseBar = () => {
    onCloseBar();
    setAnchorEl(null);
    setActiveSubMenu(null);
  };

  if (!selectedItemsCount || !isLgWidth) {
    return null;
  }

  return (
    <Box sx={styles.toolbarWrapper}>
      <Toolbar sx={(muiTheme) => styles.toolbar(muiTheme, buttons.length)}>
        <Box sx={[styles.item, styles.countItem]}>
          <Typography variant="subtitle1-bold" component="p" fontSize="40px !important" color="inherit">
            {selectedItemsCount}
          </Typography>
        </Box>

        <Box sx={[styles.item, styles.textItem]}>
          <Typography variant="subtitle1-bold" component="p" color="inherit">
            {`${selectedItemsCount > 1 ? 'Users' : 'User'} selected`}
          </Typography>
        </Box>

        {barButtons.map(({ Icon, label, subItems, onClick }) => {
          const ariaDetails = subItems.length ? subItems.map(({ subItemId }) => subItemId).join(' ') : undefined;
          const isSubMenuOpen = !!subItems.length && !!anchorEl && activeSubMenu === label;

          return (
            <Fragment key={label}>
              <Button
                aria-details={ariaDetails}
                onClick={(event) => handleButtonClick(event, onClick, subItems, label)}
                sx={styles.item}
              >
                <Box component={Icon} sx={styles.buttonIcon} />

                <Typography variant="body2-bold" component="span" color="inherit">
                  {label}
                </Typography>
              </Button>

              {!!subItems.length && (
                <Popper role="presentation" open={isSubMenuOpen} anchorEl={anchorEl}>
                  <List disablePadding sx={styles.subMenu}>
                    {subItems.map(({ subItemId, label: subItemLabel, onClick: subItemOnClick }) => (
                      <ListItem key={subItemId} disablePadding divider sx={styles.subItem}>
                        <ListItemButton
                          onClick={(event) => handleButtonClick(event, subItemOnClick)}
                          sx={styles.subItemButton}
                        >
                          <ListItemText
                            primary={subItemLabel}
                            primaryTypographyProps={{
                              id: subItemId,
                              variant: 'body2',
                              color: 'inherit',
                              textAlign: 'center',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Popper>
              )}
            </Fragment>
          );
        })}

        <Button sx={[styles.item, styles.closeButton]} onClick={handleCloseBar}>
          <Box component={CloseIcon} sx={styles.buttonIcon} />
        </Button>
      </Toolbar>
    </Box>
  );
};

const buttonPropTypes = {
  Icon: PropTypes.func,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

StickyBar.propTypes = {
  selectedItemsCount: PropTypes.number.isRequired,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      ...buttonPropTypes,
      subItems: PropTypes.arrayOf(PropTypes.shape(buttonPropTypes)),
    }),
  ).isRequired,
  onCloseBar: PropTypes.func.isRequired,
};
